/**
 * Application route constants
 * Centralized route paths for type-safe navigation
 */

export const ROUTES = {
  // Root
  ROOT: '/',
  INDEX: '/index',
  NOT_FOUND: '/404',
  
  // Public pages
  EXPERTS: '/experts',
  HELP_CENTER: '/help-center',
  EXPLORE: '/explore',
  FAVORITES: '/favorites',
  CHAT: '/chat',
  
  // Topics (listing and detail)
  TOPICS: '/topics',
  TOPIC_PAGE: '/topics/:topicSlug',
  
  // Auth
  AUTH: '/auth',
  AUTH_LOGIN: '/auth',
  AUTH_SIGNUP: '/auth?tab=signup',
  AUTH_RESET_PASSWORD: '/auth?tab=reset',
  AUTH_CALLBACK: '/auth/callback',
  RESET_PASSWORD: '/reset-password',
  
  // Expert - DEPRECATED: Use flat /{vanityUrl} via DeterministicRouter
  // This route exists only for 301 redirect to canonical /{vanityUrl}
  EXPERT_PROFILE: '/experts/:vanityUrl',
  EXPERT_REGISTER: '/expert-register',
  
  // REMOVED: PUBLICATION_DETAIL - publications are accessed via /:vanityUrl/:slug format

  // Content
  PUBLICATION_DETAIL: '/publications/:slug',
  
  // Localized pages (resolved by DeterministicRouter via manifest)
  // These are institutional/static pages with localized slugs
  // Usage: getPath('about-us'), getPath('methodology'), etc.
  // Note: Not registered as explicit <Route> entries - handled by manifest-based catch-all
  
  // Account
  ACCOUNT: '/account',
  ACCOUNT_OVERVIEW: '/account',
  ACCOUNT_PERFORMANCE: '/account/performance',
  ACCOUNT_OPPORTUNITIES: '/account/opportunities',
  ACCOUNT_OPPORTUNITIES_INBOX: '/account/opportunities?tab=inbox',
  ACCOUNT_OPPORTUNITIES_OUTBOX: '/account/opportunities?tab=outbox',
  ACCOUNT_CHATS: '/account/chats',
  ACCOUNT_CHATS_PROJECTS: '/account/chats/projects',
  ACCOUNT_FAVORITES: '/account/favorites',
  // Publications (formerly Publications)
  ACCOUNT_PUBLICATIONS: '/account/publications',
  ACCOUNT_PUBLICATIONS_NEW: '/account/publications/new',
  ACCOUNT_PUBLICATION_EDITOR: '/account/publications/:publicationId',
  ACCOUNT_PROFILE: '/account/profile',
  ACCOUNT_SETTINGS: '/account/preferences',
  ACCOUNT_MEMBERSHIP: '/account/preferences?tab=membership',
  ACCOUNT_MEMBERSHIP_ORDERS: '/account/preferences?tab=membership&subtab=orders',
  ACCOUNT_MEMBERSHIP_METHODS: '/account/preferences?tab=membership&subtab=methods',
  ACCOUNT_COMMENTS: '/account/comments',
  ACCOUNT_MEDIA: '/account/media',
  
  // Admin - Root
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin',
  
  // Admin - Operations (path-based tabs)
  ADMIN_OPERATIONS: '/admin/operations',
  ADMIN_OPERATIONS_HELP_CENTER: '/admin/operations/help-center',
  ADMIN_OPERATIONS_ANALYTICS: '/admin/operations/analytics',
  ADMIN_OPERATIONS_INDEXATION: '/admin/operations/indexation',
  ADMIN_OPERATIONS_OPPORTUNITY_DETAIL: '/admin/operations/opportunities/:opportunityId',
  
  // Admin - Intelligence (query-param tabs - parent route handles all)
  ADMIN_INTELLIGENCE: '/admin/intelligence',
  ADMIN_INTELLIGENCE_ANALYTICS: '/admin/intelligence?tab=analytics',
  ADMIN_INTELLIGENCE_CONTENT_REVIEW: '/admin/intelligence?tab=content-review',
  ADMIN_INTELLIGENCE_KNOWLEDGE: '/admin/intelligence?tab=knowledge',
  ADMIN_INTELLIGENCE_STRATEGY: '/admin/intelligence?tab=strategy',
  ADMIN_INTELLIGENCE_AGENTS: '/admin/intelligence?tab=ai-platform',
  
  // Admin - Management (query-param tabs - parent route handles all)
  ADMIN_MANAGEMENT: '/admin/management',
  ADMIN_MANAGEMENT_PERFORMANCE: '/admin/management?tab=performance',
  ADMIN_MANAGEMENT_USERS: '/admin/management?tab=users',
  ADMIN_MANAGEMENT_LOCALIZATION: '/admin/intelligence?tab=localization',
  ADMIN_MANAGEMENT_WORKFLOWS: '/admin/management?tab=observer&observerTab=automation',
  ADMIN_MANAGEMENT_AUTOMATION: '/admin/management?tab=observer&observerTab=automation',
  ADMIN_MANAGEMENT_UI_SEED: '/admin/intelligence?tab=localization&locSubtab=ui&uiTab=defaultvalues',
  ADMIN_MANAGEMENT_UI_TRANSLATE: '/admin/intelligence?tab=localization&locSubtab=ui&uiTab=editor',
  ADMIN_MANAGEMENT_GLOSSARY: '/admin/intelligence?tab=topics&subtab=glossary',
  ADMIN_MANAGEMENT_SETTINGS: '/admin/management?tab=settings',
  ADMIN_MANAGEMENT_BILLING: '/admin/management?tab=settings&settingsTab=plans',
  ADMIN_MANAGEMENT_EMAILS: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=system&systemTab=emails',
  ADMIN_MANAGEMENT_PAGES: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=system&systemTab=pages',
  ADMIN_MANAGEMENT_ROUTES: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=system&systemTab=routes',
  ADMIN_MANAGEMENT_LOCATIONS: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=system&systemTab=locations',
  ADMIN_MANAGEMENT_QA: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=qa',
  ADMIN_MANAGEMENT_FAQS: '/admin/intelligence?tab=localization&locSubtab=metadata&metadataTab=system&systemTab=faqs',
  ADMIN_MANAGEMENT_PAGE_EDITOR: '/admin/management/pages/:pageId',
  ADMIN_MANAGEMENT_PAGE_NEW: '/admin/management/pages/new',
  
  // Admin - Unified Entity Editor
  ADMIN_MANAGEMENT_ENTITY_EDITOR: '/admin/management/entities/:entityType/:entityId',

  // Admin - Topic editor (full page, publication-style)
  ADMIN_TOPIC_EDITOR: '/admin/topics/:topicId',
  
  // Admin - Observer (query-param tabs within Management)
  ADMIN_MANAGEMENT_OBSERVER: '/admin/management?tab=observer',
  ADMIN_MANAGEMENT_OBSERVER_STATUS: '/admin/management?tab=observer&observerTab=status',
  ADMIN_MANAGEMENT_OBSERVER_SERVICES: '/admin/management?tab=observer&observerTab=services',
  ADMIN_MANAGEMENT_OBSERVER_JOBS: '/admin/management?tab=observer&observerTab=jobs',
  ADMIN_MANAGEMENT_OBSERVER_NOTIFICATIONS: '/admin/management?tab=observer&observerTab=notifications',
  ADMIN_MANAGEMENT_OBSERVER_LOGS: '/admin/management?tab=observer&observerTab=logs',
  
  // Admin - Other standalone pages
  ADMIN_USERS: '/admin/operations?tab=users',
  ADMIN_ROLES: '/admin/roles',
  ADMIN_ACTIVITY: '/admin/activity',
  ADMIN_OPPORTUNITIES: '/admin/opportunities',
  ADMIN_DESIGN_SYSTEM: '/admin/design-system',
  // DEPRECATED: QA Fix Center moved to Localization tab - use ADMIN_MANAGEMENT_QA instead
  // ADMIN_QA_FIX_CENTER: '/admin/qa/fix-center',
  
  // Billing / Plans
  PLANS: '/plans',
  CHECKOUT: '/checkout',
  CHECKOUT_CONFIRMATION: '/checkout/confirmation',
  
  // Other
  VERIFY_OPPORTUNITY_EMAIL: '/verify-opportunity-email',
  CHAT_SESSION: '/chat/:sessionId',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = typeof ROUTES[RouteKey];
